import { PriceTier, serializePriceTier } from '../../../models/PriceTier';
import { BulkAddProductsToSelectionListResponse, CollectionSearchScope } from '../../store/api';
import { useTranslation } from 'react-i18next';
import { TFunction } from 'i18next';
import { useDispatch } from 'react-redux';
import { useState } from 'react';
import { SubscriptionPeriodType } from '../../../models/Article';
import { deserializeFilter } from '../../shared/utils/deserializeFilter';
import { SOURCE_COLLECTION } from '../../store/constants';
import { addBulkFromCollectionSearchByFilter } from './addBulkFromCollectionSearchByFilter';
import { bulkUpdateToList } from './bulkUpdateToList';
import { BulkAddProductsByCollectionSearchRequest } from '../shopping-cart/addProductsFromCollectionSearchByFilter';

type GetRequestParams = {
    selectionListId: string;
    query: string;
    refine?: string;
    filter: string;
    priceTier: PriceTier;
    performChecks: boolean;
    scope: CollectionSearchScope;
};

const buildAddProductsCallback = (
    { selectionListId, query, refine, scope, filter, priceTier, performChecks }: GetRequestParams,
    t: TFunction
): (() => Promise<BulkAddProductsToSelectionListResponse>) => {
    return () => {
        try {
            const body: BulkAddProductsByCollectionSearchRequest = {
                query,
                refine,
                scope,
                filterRequest: deserializeFilter(filter, {
                    includeKey: 'include',
                    excludeKey: 'exclude'
                }),
                targetPrice: serializePriceTier(priceTier)!,
                subscriptionPeriodType: SubscriptionPeriodType.CURRENT
            };

            return addBulkFromCollectionSearchByFilter({ selectionListId, body, params: { performChecks } });
        } catch (e) {
            throw new Error(t('notifications.buildAddProductsCallback.error'));
        }
    };
};

type Props = {
    filter: string;
    scope: CollectionSearchScope;
    query: string;
    refine?: string;
};

export const useAddFromCollectionSearchToList = ({ filter, scope, query, refine }: Props) => {
    const { t } = useTranslation();
    const dispatchRedux = useDispatch();
    const [isLoading, setLoading] = useState(false);

    return {
        isLoading,
        addFromSearchToListBulk: (listId: string, priceTier: PriceTier, performChecks: boolean) => {
            const apiCallback = buildAddProductsCallback(
                {
                    selectionListId: listId,
                    query,
                    refine,
                    filter,
                    scope,
                    priceTier,
                    performChecks
                },
                t
            );

            return bulkUpdateToList({
                dispatch: dispatchRedux,
                setLoading,
                performChecks,
                source: SOURCE_COLLECTION,
                apiCallback,
                t
            });
        }
    };
};
